<script setup>
import FormLabel from '~/components/Forms/Partials/FormLabel.vue'
import FormHelpText from '~/components/Forms/Partials/FormHelpText.vue'
import FormErrorMsg from '~/components/Forms/Partials/FormErrorMsg.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  readonly: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  errorMsg: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  rows: {
    type: Number,
    default: 3
  },
  resizeable: {
    type: Boolean,
    default: true
  },
  showCheckIcon: {
    type: Boolean,
    default: false
  },
  inputClasses: {
    type: [String, Array, Object],
    default: ''
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const hasError = computed(() => !!props.errorMsg)
const hasValue = computed(() => props.modelValue !== '' && props.modelValue != null)
const isSuccess = computed(() => hasValue.value && !hasError.value && props.showCheckIcon)

const textareaId = computed(() => props.name || props.label?.toLowerCase().replace(/\s+/g, '_') || `textarea_${Math.random().toString(36).substring(2, 9)}`)

const baseInputClasses = computed(() => [
  'w-full form-control-textarea',
  (hasError.value || isSuccess.value) ? 'pr-10' : '',
  props.resizeable ? 'resize-y' : 'resize-none',
  hasError.value ? 'form-control-error' : '',
  props.inputClasses
])

const inputRef = ref(null)

// Exponer el textarea para focus externo
defineExpose({
  focus: () => inputRef.value?.focus(),
  $el: computed(() => inputRef.value)
})
</script>

<template>
  <div class="block">
    <slot name="beforeLabel" />

    <FormLabel v-if="label" :for="textareaId" :required="required">
      {{ label }}
    </FormLabel>

    <slot name="beforeInput" />

    <div class="relative">
      <slot name="inputPrefix" />

      <textarea
        :id="textareaId"
        ref="inputRef"
        v-model="value"
        :name="name"
        :placeholder="placeholder"
        :rows="rows"
        :disabled="disabled"
        :readonly="readonly"
        :autofocus="autofocus"
        :class="baseInputClasses"
      ></textarea>

      <Icon
        v-if="isSuccess"
        name="mdi:check-circle"
        class="absolute right-3 top-3 text-green-500 text-sm pointer-events-none"
      />
      <Icon
        v-if="hasError"
        name="mdi:alert-circle"
        class="absolute right-3 top-3 text-red-500 text-sm pointer-events-none"
      />

      <slot name="inputSuffix" />
    </div>

    <slot name="afterInput" />

    <FormErrorMsg v-if="errorMsg" :text="errorMsg" />

    <FormHelpText v-else-if="helpText" :text="helpText" />
  </div>
</template>
